import React from "react";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";

const scoreChip = (label, value) => (
    <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold uppercase text-slate-700">
        {label}:<span className="text-brand-blue-800">{value ?? "—"}</span>
    </span>
);

const rtsChip = (value) => (
    <span
        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold uppercase ${
            value === true
                ? "bg-green-100 text-green-800"
                : value === false
                ? "bg-red-100 text-red-800"
                : "bg-slate-100 text-slate-700"
        }`}
    >
        RTS:{value === true ? "Y" : value === false ? "N" : "—"}
    </span>
);

const LEVEL_STYLES = {
    critical: "bg-red-100 text-red-800 border-red-200",
    overdue: "bg-orange-100 text-orange-800 border-orange-200",
    due: "bg-amber-100 text-amber-800 border-amber-200",
    current: "bg-brand-blue-50 text-brand-blue-800 border-brand-blue-200",
};

const formatEventAt = (iso, kind) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    const dateStr = `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
    if (kind === "measurement") {
        const time = d.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        return `${dateStr} · ${time}`;
    }
    return dateStr;
};

const HistoryItem = ({ item, index, showCaregiver, onOpen }) => {
    const isMeasurement = item.kind === "measurement";
    const isBacklog = item.kind === "backlog";
    const isCurrent = item.kind === "current";
    const meta = item.backlog;

    let ButtonIcon = CheckCircle2;
    let borderColor = "border-green-200";
    let statusBadge = null;

    if (isBacklog) {
        ButtonIcon = AlertTriangle;
        borderColor = "border-orange-200";
        const label = (meta?.level || "overdue").toUpperCase();
        statusBadge = (
            <span
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${LEVEL_STYLES[meta?.level] || LEVEL_STYLES.overdue}`}
            >
                {label} · {(meta?.days_overdue ?? 0).toFixed(0)}d overdue
            </span>
        );
    } else if (isCurrent) {
        ButtonIcon = Clock;
        borderColor = "border-brand-blue-200";
        const freq = item.task?.frequency || "daily";
        const dueLabel =
            freq === "weekly"
                ? "Due this week"
                : freq === "monthly"
                ? "Due this month"
                : "Due today";
        statusBadge = (
            <span
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${LEVEL_STYLES.current}`}
            >
                {dueLabel}
            </span>
        );
    } else if (isMeasurement) {
        statusBadge = (
            <span className="inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-green-800">
                Scored
            </span>
        );
    }

    return (
        <div
            data-testid={`history-item-${item.kind}-${index}`}
            className={`rounded-xl border ${borderColor} bg-white p-4 shadow-sm`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                        {formatEventAt(item.event_at, item.kind)}
                    </p>
                    <h3 className="mt-1 line-clamp-2 text-sm font-bold text-slate-900">
                        {item.task?.title}
                    </h3>
                    <p className="mt-0.5 text-xs text-slate-500">
                        {item.individual?.first_name} {item.individual?.last_name}
                        {item.outcome?.life_area
                            ? ` · ${item.outcome.life_area}`
                            : ""}
                    </p>
                    {showCaregiver && item.caregiver && (
                        <p
                            className="mt-1 text-[11px] font-semibold text-brand-blue-700"
                            data-testid="history-item-scoredby"
                        >
                            Scored by: {item.caregiver.first_name}{" "}
                            {item.caregiver.last_name}
                        </p>
                    )}
                </div>
                <div className="shrink-0">{statusBadge}</div>
            </div>

            {isMeasurement && item.measurement && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {scoreChip("D", item.measurement.disposition_score)}
                    {scoreChip("Q", item.measurement.quality_score)}
                    {scoreChip("A", item.measurement.autonomy_score)}
                    {rtsChip(item.measurement.response_to_supports)}
                </div>
            )}

            {isMeasurement && item.measurement?.comment_text && (
                <p className="mt-2 line-clamp-2 text-xs text-slate-600">
                    <span className="font-semibold text-slate-500">Comment: </span>
                    {item.measurement.comment_text}
                </p>
            )}

            <div className="mt-3 flex justify-end">
                <button
                    type="button"
                    onClick={() => onOpen(item)}
                    data-testid={`history-item-open-${index}`}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                        isMeasurement
                            ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            : "bg-brand-blue-700 text-white hover:bg-brand-blue-800"
                    }`}
                >
                    <ButtonIcon className="h-3.5 w-3.5" />
                    {isMeasurement ? "View / Edit" : "Score now"}
                </button>
            </div>
        </div>
    );
};

export default HistoryItem;
